import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {MatExpansionPanel} from '@angular/material/expansion';
import {MatSelectChange} from '@angular/material/select';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {Router} from '@angular/router';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {BmpProductionScheduleItemInPathGroup} from '@domain/production-schedule/bmp-production-schedule-item-in-path-group';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {EpProductionScheduleItemInPathGroup} from '@domain/production-schedule/ep-production-schedule-item-in-path-group';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleDisplay} from '@domain/production-schedule/production-schedule-display.enum';
import {ProductionScheduleDrawingViewMode} from '@domain/production-schedule/production-schedule-drawing-view-mode.enum';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {PropertyValue} from '@domain/property-value';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {PathWidth} from '@domain/textile-data/machine-quality/path-width';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {MACHINE_QUALITIES, MachineQualities} from '@infrastructure/http/machine-quality/machine-qualities';
import {PRODUCTION_SCHEDULES, ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {ProductionScheduleBuilderPageStateManagerService} from '@infrastructure/page-state-manager/production-schedule-builder/production-schedule-builder-page-state-manager.service';
import {ProductionScheduleNewBuilderPageState} from '@infrastructure/page-state-manager/production-schedule-builder/production-schedule-new-builder-page-state.interface';
import {SwitchCommercialTechnicalUnitsCustomSettingsDialogComponent} from '@presentation/components/dialog-button/dialog-data/switch-commercial-technical-units/switch-commercial-technical-units.component';
import {SideNavigationStateService} from '@presentation/navigation/side-navigation-state.service';
import {ProductionScheduleBuilderSummaryComponent} from '@presentation/pages/texfab/production-schedule/add/plan/builder-summary/production-schedule-builder-summary.component';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {
  AssertionUtils,
  BaseComponent,
  convertCommercialUnit,
  convertDentsToWidthInMeter,
  convertHeightInMeterToPicks,
  convertPicksToHeightInMeter,
  convertUnit,
  DOMUtils,
  TranslateService,
  Unit,
  WINDOW
} from '@vdw/angular-component-library';
import {assign, find, isEmpty, isEqual, noop, round} from 'lodash-es';
import {Observable, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {PathContentSettingsDialogData} from '../menu-items-new/path-content-settings-dialog-data.interface';
import {PathBarComponent} from './builder/path-item/path-bar.component';
import {PathLayoutComponent} from './builder/path-layout/path-layout.component';
import {ProductionScheduleListviewComponent} from './new-listview/production-schedule-listview.component';
import {ResizeDirection} from './resize-direction.enum';
import {ProductionScheduleVerifyComponent} from './verify/production-schedule-verify.component';

@Component({
  selector: 'app-production-schedule-plan',
  templateUrl: './production-schedule-plan.component.html',
  styleUrls: ['./production-schedule-plan.component.scss']
})
export class ProductionSchedulePlanComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit, AfterContentChecked {
  public static readonly PADDING_WIDTH_IN_PX = 24;
  public static readonly MARGIN_WIDTH_IN_PX = 16;

  private static readonly urlToProductionScheduleOverview = NavigationUtils.getAbsolutePath(NavigationId.CARPET_PRODUCTION_ORDER);

  @Input() public customSettings: ProductionOrderCustomSettings;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public pathLabelsOrNonProductionItemsFromLongestPathLengthInPicks: number;
  @Input() public pathLabelsOrNonProductionItemsFromLongestPathLengthInCommercialUnit: number;
  @Input() public lastProductionSchedulePhase: ProductionSchedulePhase;
  @Input() public isVerifyingFromConfigurePhase: boolean;
  @Input() public pathWidths: PathWidth[];
  @Input() public listOfMachineQualityCustomSettings: PropertyValue[];
  @Input() public pathContentSettingsDialogData: PathContentSettingsDialogData;
  @Input() public preselectedOrderlineIds: number[] = [];
  @Input() public preselectedDesignIds: number[] = [];
  @Input() public preselectedConfigurationIds: number[] = [];

  @Output() public returnToProductionSchedulePhase: EventEmitter<ProductionSchedulePhase> = new EventEmitter<ProductionSchedulePhase>();
  @Output() public productionSchedulePlanChanged: EventEmitter<void> = new EventEmitter<void>();
  @Output() public verifiedProductionSchedule: EventEmitter<{confirmImmediately: boolean; error: boolean}> = new EventEmitter<{confirmImmediately: boolean; error: boolean}>();
  @Output() public navigateBackOutput = new EventEmitter<any>();

  @ViewChild('productionScheduleBuilderSummary') public productionScheduleBuilderSummary: ProductionScheduleBuilderSummaryComponent;
  @ViewChild(MatExpansionPanel) public expansionPanel: MatExpansionPanel;
  @ViewChild('productionSchedulePlanWrapper') public productionSchedulePlanWrapper: ElementRef<HTMLElement>;
  @ViewChild('builderContainer') public builderContainer: ElementRef;
  @ViewChild('buttonsContainer') public buttonsContainer: ElementRef;
  @ViewChild('errorsAndWarningsContainer') public errorsAndWarningsContainer: ElementRef;
  @ViewChild('zoomOutContainer', {read: ElementRef}) public zoomOutContainer: ElementRef;
  @ViewChild('zoomInContainer', {read: ElementRef}) public zoomInContainer: ElementRef;
  @ViewChild('editPathsToggleContainer', {read: ElementRef}) public editPathsToggleContainer: ElementRef;
  @ViewChild('viewSelectorContainer', {read: ElementRef}) public viewSelectorContainer: ElementRef;
  @ViewChild('drawingLibrary', {read: ElementRef}) public drawingLibrary: ElementRef<HTMLElement>;
  @ViewChild('pathLayout', {read: ElementRef}) public pathLayout: ElementRef<HTMLElement>;
  @ViewChild(PathLayoutComponent) private pathLayoutComponent: PathLayoutComponent;
  @ViewChild('productionScheduleNewListView') public productionScheduleNewListViewComponent: ProductionScheduleListviewComponent;

  public readonly resizeDirection: typeof ResizeDirection = ResizeDirection;
  public selectedProductionScheduleDisplay: ProductionScheduleDisplay = ProductionScheduleDisplay.DETAILVIEW;
  public productionScheduleDisplay: typeof ProductionScheduleDisplay = ProductionScheduleDisplay;
  public isVerifyingProductionSchedule = false;
  public buggyLengthMinInPicks: number;
  public buggyLengthMaxInPicks: number;
  public buggyLengthMaxInMM: number;
  public availableSpaceInBuggyInPicks: number;
  public availableSpaceInBuggyInCommercialUnit: number;
  public availableSpaceInBuggyForBuilderInPicks: number;
  public availableSpaceInBuggyForBuilderInCommercialUnit: number;
  public canZoomIn: boolean = true;
  public canZoomOut: boolean = true;
  public zoomLevel: number = 1;
  public displayTechnicalUnit: boolean = true;
  public isResizing: boolean = false;
  public readonly resizerPx: number = 16;
  public readonly customSettingsComponent: typeof SwitchCommercialTechnicalUnitsCustomSettingsDialogComponent = SwitchCommercialTechnicalUnitsCustomSettingsDialogComponent;
  public readonly MIN_PATH_LAYOUT_HEIGHT_IN_PX: number = 200;
  public readonly MIN_DRAWING_LIBRARY_WIDTH_IN_PX: number = 600;
  public readonly MIN_DRAWING_LIBRARY_HEIGHT_IN_PX: number = 250;
  public readonly INITIAL_PATH_LAYOUT_HEIGHT_IN_PX: number = 300;
  public commercialUnit: Unit = Unit.CENTIMETER;
  public initialPathLayoutHeightInPx: number = 300;
  public initialPathLayoutWidthInPx: number;
  public initialPathLayoutMaxHeightInPx: number;
  public minPathLayoutWidthInPx: number;
  public minPathContentWidthInPx: number;
  public canShowWarningWithText: boolean;
  public maxWarningWidth: number = 44;
  public canShowSelectedItems: boolean = false;
  public selectedPath: ProductionSchedulePath;
  public selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  public selectedViewMode: ProductionScheduleDrawingViewMode;
  public marginInDentsOfMissingDesigns: number = 0;
  public selectedPathContentRowData: ProductionScheduleItemInPathGroup;

  private productionScheduleHeight: number = 0;
  private hasProductionScheduleLoaded: boolean = false;
  private isBuildingProductionSchedule: boolean = false;
  private _productionSchedulePhase: ProductionSchedulePhase;
  private productionScheduleBuilderState: ProductionScheduleNewBuilderPageState;
  private readonly zoomFactor: number = 0.1;
  private readonly zoomInClickSubject: Subject<void> = new Subject<void>();
  private readonly zoomOutClickSubject: Subject<void> = new Subject<void>();
  private readonly defaultCustomSettingsDialogData = {
    labelTranslationKey: this.translate.instant('GENERAL.UNIT.LABEL', {object: this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.BUILDER')}),
    technicalDimensionsTranslationKey: 'PRODUCTION_ORDER.WEAVE_PLAN.DENTS_AND_PICKS'
  };

  private currentSubscription: Subscription;

  private cancelMouseMoveListener: () => void;
  private cancelMouseUpListener: () => void;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(PRODUCTION_SCHEDULES) private readonly productionSchedules: ProductionSchedules,
    @Inject(MACHINE_QUALITIES) private readonly machineQualities: MachineQualities,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    @Inject(WINDOW) private readonly window: Window,
    private readonly router: Router,
    private readonly renderer: Renderer2,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly zone: NgZone,
    private readonly sideNavigationStateService: SideNavigationStateService,
    private readonly translate: TranslateService,
    private readonly productionScheduleBuilderPageStateManager: ProductionScheduleBuilderPageStateManagerService
  ) {
    super();
  }

  public get productionSchedulePhase(): ProductionSchedulePhase {
    return this._productionSchedulePhase;
  }

  @Input()
  public set productionSchedulePhase(productionSchedulePhase: ProductionSchedulePhase) {
    this._productionSchedulePhase = productionSchedulePhase;

    if (productionSchedulePhase === ProductionSchedulePhase.BUILD && !this.isVerifyingFromConfigurePhase) {
      this.isBuildingProductionSchedule = true;
      this.getProductionScheduleBuilderPageState();
      setTimeout(() => {
        this.isBuildingProductionSchedule = false;
      }, 1000);
    } else if (productionSchedulePhase === ProductionSchedulePhase.VERIFY) {
      if (this.productionSchedule.isExecuted() || this.productionSchedule.completingPartially) {
        this.selectedProductionScheduleDisplay = ProductionScheduleDisplay.LISTVIEW;
      } else {
        setTimeout(() => {
          this.selectedProductionScheduleDisplay = ProductionScheduleDisplay.OVERVIEW;
        });
      }
    }
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.executeResizePathBarComponent();
  }

  public ngAfterViewInit(): void {
    if (!AssertionUtils.isNullOrUndefined(this.builderContainer)) {
      DOMUtils.observeResize(this.builderContainer.nativeElement)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy), distinctUntilChanged())
        .subscribe(() => this.setWarningTextVisibility());
    }
    this.setInitialPathLayoutDimentions();
    this.changeDetectorRef.detectChanges();
  }

  public ngAfterContentChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    if (isEmpty(this.productionSchedule.productionSchedulePathsOfColoredYarnSets)) {
      this.productionSchedule.initProductionSchedulePathsForEachColoredYarnSet(this.productionSchedule.coloredYarnSets);
    }

    this.initialiseProductionSchedulePlan();
    this.subscribeToZoomInChanges();
    this.subscribeToZoomOutChanges();
    this.subscribeToNavigationStateChanges();
    this.getQualitySettings();
  }

  public ngOnChanges(): void {
    if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.buggy)) {
      this.setAvailableSpaceInBuggy();
    }
  }

  public zoomIn(): void {
    if (this.canZoomIn) {
      this.zoomInClickSubject.next();
    }
  }

  public zoomOut(): void {
    if (this.canZoomOut) {
      this.zoomOutClickSubject.next();
    }
  }

  public isProductionSchedulePhasePreselection(): boolean {
    return this.productionSchedulePhase === ProductionSchedulePhase.PRESELECTION;
  }

  public onCanZoomInChanges(canZoomIn: boolean): void {
    this.canZoomIn = canZoomIn;
    this.changeDetectorRef.detectChanges();
  }

  public onCanZoomOutChanges(canZoomOut: boolean): void {
    this.canZoomOut = canZoomOut;
    this.changeDetectorRef.detectChanges();
  }

  public canShowDetailView(): boolean {
    return isEqual(this.selectedProductionScheduleDisplay, ProductionScheduleDisplay.DETAILVIEW);
  }

  public canShowListView(): boolean {
    return isEqual(this.selectedProductionScheduleDisplay, ProductionScheduleDisplay.LISTVIEW) && !this.isVerifyingProductionSchedule;
  }

  public canShowOverview(): boolean {
    return isEqual(this.selectedProductionScheduleDisplay, ProductionScheduleDisplay.OVERVIEW);
  }

  public canShowProductionScheduleBuilder(): boolean {
    return (this.canShowDetailView() || this.canShowOverview()) && !this.isVerifyingProductionSchedule && this.hasProductionScheduleLoaded && !this.isBuildingProductionSchedule;
  }

  public getProductionScheduleHeight(): string {
    return `${this.productionScheduleHeight}${this.commercialUnit}`;
  }

  public onProductionSchedulePlanChanged(): void {
    this.productionSchedulePlanChanged.emit();
  }

  public changedSelectedProductionScheduleDisplay(matSelectChange: MatSelectChange): void {
    this.selectedProductionScheduleDisplay = matSelectChange.value;

    if (this.productionSchedulePhase === ProductionSchedulePhase.BUILD) {
      this.updateProductionScheduleBuilderPageState();
    }
  }

  public canShowDrawingLibrary(): boolean {
    return !this.isProductionSchedulePhaseVerify() && !this.isVerifyingFromConfigurePhase;
  }

  public canEditProductionScheduleItems(): boolean {
    return this.canShowDrawingLibrary() || this.productionSchedule.completingPartially;
  }

  public completePartially(): Observable<void> {
    return this.productionScheduleNewListViewComponent.completePartially();
  }

  public getUnitDimensionsForLengthDifferenceToBuggy(): string {
    if (!AssertionUtils.isNullOrUndefined(this.availableSpaceInBuggyForBuilderInPicks)) {
      return `${this.availableSpaceInBuggyInCommercialUnit.toFixed(0)} cm / ${this.availableSpaceInBuggyInPicks} picks`;
    } else {
      let lengthInMeter = 0;
      let lengthInPicks = 0;

      this.productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
        productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.forEach((productionSchedulePath: ProductionSchedulePath) => {
          if (productionSchedulePath.totalLengthInPicks > lengthInPicks) {
            lengthInPicks = productionSchedulePath.totalLengthInPicks;
            lengthInMeter = convertCommercialUnit({from: {unit: Unit.MILLIMETER, value: productionSchedulePath.totalLengthInMM}, to: Unit.METER});
          }
        });
      });

      const lengthDifferenceInCommercialUnit = convertCommercialUnit({
        from: {unit: Unit.METER, value: convertPicksToHeightInMeter(this.buggyLengthMaxInPicks, this.productionSchedule.machineQuality.pickDensity * 1000) - lengthInMeter},
        to: this.commercialUnit
      });

      const lengthDifferenceInPicks = this.buggyLengthMaxInPicks - lengthInPicks;

      return `${lengthDifferenceInCommercialUnit.toFixed(2)} cm / ${lengthDifferenceInPicks} picks`;
    }
  }

  public getRestWidthDimensions(): string {
    let totalWidthInDents = 0;

    this.productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.forEach((productionSchedulePath: ProductionSchedulePath) => {
        totalWidthInDents += productionSchedulePath.technicalWidthInDents;
      });
    });

    const machineWidth = this.productionSchedule.machine.getTotalWidthInDents();

    return `${machineWidth - totalWidthInDents} dents`;
  }

  public getSurfaceDimensions(): string {
    let totalLengthInMeter = convertPicksToHeightInMeter(ProductionScheduleVerifyComponent.minProductionScheduleVerifyHeightInPicks, this.productionSchedule.machineQuality.pickDensity * 1000);
    let totalWidthInMeter = convertDentsToWidthInMeter(this.productionSchedule.machine.getTotalWidthInDents(), this.productionSchedule.machineQuality.reedDensity * 1000);

    return `${Math.round(totalLengthInMeter * totalWidthInMeter).toFixed(2)}m²`;
  }

  public getPracticalPickDensity(): string {
    return `${Math.round(this.productionSchedule.practicalPickDensityInPicksPerMM * 1000)} p/m`;
  }

  public getNonProductionItemsDimensions(): string {
    let totalLengthInMeter = convertPicksToHeightInMeter(ProductionScheduleVerifyComponent.minProductionScheduleVerifyHeightInPicks ?? 5000, this.productionSchedule.machineQuality.pickDensity * 1000);
    let totalWidthInMeter = convertDentsToWidthInMeter(this.productionSchedule.machine.getTotalWidthInDents(), this.productionSchedule.machineQuality.reedDensity * 1000);

    let totalSquareMeters = totalLengthInMeter * totalWidthInMeter;
    let totalSquareMetersOfNonProductionItems = 0;

    this.productionSchedule.productionSchedulePathsOfColoredYarnSets.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.forEach((productionSchedulePath: ProductionSchedulePath) => {
        productionSchedulePath.pathGroups.forEach((pathGroup: ProductionScheduleItemInPathGroup) => {
          if (!(pathGroup instanceof BmpProductionScheduleItemInPathGroup) && !(pathGroup instanceof EpProductionScheduleItemInPathGroup)) {
            const totalSquareMetersOfPathGroup =
              convertCommercialUnit({from: {unit: Unit.MILLIMETER, value: pathGroup.commercialDimensionsInMM.widthInMM}, to: Unit.METER}) *
              convertCommercialUnit({from: {unit: Unit.MILLIMETER, value: pathGroup.commercialDimensionsInMM.heightInMM}, to: Unit.METER});
            totalSquareMetersOfNonProductionItems += totalSquareMetersOfPathGroup;
          }
        });
      });
    });

    let totalPercentageOfNonProductionItems = (totalSquareMetersOfNonProductionItems / totalSquareMeters) * 100;

    return `${totalSquareMetersOfNonProductionItems.toFixed(2)}m² or ${totalPercentageOfNonProductionItems.toFixed(1)}%`;
  }

  public verifyProductionSchedule(confirmImmediately: boolean = false): void {
    if (!this.isVerifyingProductionSchedule) {
      this.productionSchedulePlan.updateEditingPaths(false);
      this.isVerifyingProductionSchedule = true;

      this.productionSchedules
        .verifyProductionSchedule(this.productionSchedule.id as string)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe({
          next: (productionSchedule: ProductionSchedule) => {
            this.modifyProductionScheduleAfterVerify(productionSchedule);
            this.isVerifyingProductionSchedule = false;
            this.verifiedProductionSchedule.emit({confirmImmediately, error: false});
          },
          error: () => {
            this.isVerifyingProductionSchedule = false;
            this.verifiedProductionSchedule.emit({confirmImmediately, error: true});
          }
        });
    }
  }

  public isProductionSchedulePhaseVerify(): boolean {
    return isEqual(this.productionSchedulePhase, ProductionSchedulePhase.VERIFY) && !this.isVerifyingProductionSchedule;
  }

  public back(): void {
    if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.buggy)) {
      this.setAvailableSpaceInBuggy();
    }

    if (this.isProductionSchedulePhaseVerify()) {
      this.productionSchedule.removePathLabelsAndNonProductionItemsFromProductionSchedulePathsOfColoredYarnSets();
      this.productionSchedulePlan.cancelOngoingRecoloring();
      this.productionSchedulePlan.initialiseProductionSchedule(this.productionSchedule).subscribe({next: noop, error: this.handleBackendError()});

      this.returnToProductionSchedulePhase.emit(ProductionSchedulePhase.BUILD);
    } else {
      this.returnToProductionSchedulePhase.emit(ProductionSchedulePhase.CONFIGURE);
    }
  }

  public canShowIsWeavePlanLoadingIcon(): boolean {
    return !this.hasProductionScheduleLoaded && !this.productionSchedule.isExecuted();
  }

  public canShowIsWeavePlanVerifyingIcon(): boolean {
    return (this.isVerifyingProductionSchedule || this.isVerifyingFromConfigurePhase) && this.hasProductionScheduleLoaded;
  }

  public canShowIsWeavePlanBuildingIcon(): boolean {
    return this.isBuildingProductionSchedule && this.hasProductionScheduleLoaded;
  }

  public scrollIntoWeavePlanSummary(): void {
    const expansionPanelHeader = document.getElementById(this.expansionPanel._headerId);

    const productionSchedulePlanWrapperRect = this.productionSchedulePlanWrapper.nativeElement.getBoundingClientRect();
    const expansionPanelHeaderRect = expansionPanelHeader.getBoundingClientRect();

    this.productionSchedulePlanWrapper.nativeElement.scrollTo({
      top: expansionPanelHeaderRect.top - productionSchedulePlanWrapperRect.top,
      behavior: 'smooth'
    });
  }

  public canShowErrorsAndWarnings(): boolean {
    return (
      !AssertionUtils.isNullOrUndefined(this.productionSchedule.warnings?.totalLossInPercent) ||
      this.productionSchedule.isCreelMappingApplied() ||
      (this.isProductionSchedulePhaseVerify() &&
        (!AssertionUtils.isNullOrUndefined(this.productionSchedule.errors?.totalWidthTooBig) || !AssertionUtils.isNullOrUndefined(this.productionSchedule.errors?.totalWidthTooLittle)))
    );
  }

  public getProductionSchedulePracticalPickDensity(): string {
    const defaultUnit: PropertyValue = find(this.listOfMachineQualityCustomSettings, ['propertyName', 'pickDensityUnit']).propertyValue;

    const practicalPickDensity = convertUnit({
      from: {
        value: this.productionSchedule.practicalPickDensityInPicksPerMM,
        unit: Unit.PICKS_PER_MILLIMETER
      },
      to: Unit[`${defaultUnit}`]
    });

    return `${round(practicalPickDensity)}${Unit[`${defaultUnit}`]}`;
  }

  public onResizerMouseDown(event: MouseEvent, direction: ResizeDirection): void {
    if (event.button === 0) {
      this.isResizing = true;

      this.zone.runOutsideAngular(() => {
        this.cancelMouseMoveListener = this.renderer.listen(this.window, 'mousemove', (mouseMoveEvent: MouseEvent) => this.onResizerMouseMove(mouseMoveEvent, direction));

        this.cancelMouseUpListener = this.renderer.listen(this.window, 'mouseup', () => {
          this.cancelListeners();
        });
      });
    }
  }

  public onEditProductionSchedulePathsInColoredYarnSetToggle(event: MatSlideToggleChange): void {
    this.productionSchedulePlan.updateEditingPaths(event.checked);
  }

  public getInitialPathLayoutWidthInPx(): string {
    return this.canShowPathContent() ? `${this.initialPathLayoutWidthInPx}px` : '100%';
  }

  public canShowPathContent(): boolean {
    return this.pathContentSettingsDialogData.pathContentPanelEnabled;
  }

  public onPathContentSettingsDialogDataChanged(): void {
    this.executeResizePathBarComponent();
    setTimeout(() => {
      this.pathLayoutComponent?.setMaxWidthForDataElements();
    });
  }

  public onHoverPathBarOutput(): void {
    this.cancelListeners();
  }

  public hasPermissionToNewVerifyStep(): boolean {
    return this.currentSubscription?.hasPermission(Permission.TEXFAB_VERIFY_NEW);
  }

  private cancelListeners(): void {
    this.isResizing = false;
    this.updateProductionScheduleBuilderPageState();
    this.executeResizePathBarComponent();

    if (this.cancelMouseMoveListener) {
      this.cancelMouseMoveListener();
      this.cancelMouseMoveListener = null;
    }

    if (this.cancelMouseUpListener) {
      this.cancelMouseUpListener();
      this.cancelMouseUpListener = null;
    }
  }

  private executeResizePathBarComponent(): void {
    setTimeout(() => {
      this.pathLayoutComponent?.pathBars.forEach((pathBar: PathBarComponent) => {
        pathBar.resizePathBarComponent();
      });
    });
  }

  private updatePathLabelHeight(pathLabelHeightInPx: number): void {
    this.renderer.setStyle(this.pathLayout.nativeElement, 'height', `${pathLabelHeightInPx}px`);
  }

  private updatePathLayoutWidth(pathLayoutWidthInPx: number): void {
    this.renderer.setStyle(this.pathLayout.nativeElement, 'width', `${pathLayoutWidthInPx}px`);
  }

  private onResizerMouseMove(mouseMoveEvent: MouseEvent, direction: ResizeDirection): void {
    if (this.isResizing) {
      if (direction === ResizeDirection.VERTICAL) {
        this.updatePathLabelHeight(mouseMoveEvent.y - this.pathLayout.nativeElement.getBoundingClientRect().top);
      } else if (direction === ResizeDirection.HORIZONTAL) {
        this.updatePathLayoutWidth(mouseMoveEvent.x - this.drawingLibrary.nativeElement.getBoundingClientRect().left);
      }
    }
  }

  private getZoomLevel(): number {
    return round(this.zoomLevel * this.zoomFactor, 2);
  }

  private subscribeToZoomInChanges(): void {
    this.zoomInClickSubject.pipe(debounceTime(250), takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      this.zoomLevel = round(this.zoomLevel + this.getZoomLevel(), 2);
    });
  }

  private subscribeToZoomOutChanges(): void {
    this.zoomOutClickSubject.pipe(debounceTime(250), takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      this.zoomLevel = round(this.zoomLevel - this.getZoomLevel(), 2);
    });
  }

  private initialiseProductionSchedulePlan(): void {
    this.canShowSelectedItems = this.productionSchedule.hasAddedDesigns() || this.productionSchedule.hasOrderLines();

    this.productionSchedulePlan.initialiseProductionSchedule(this.productionSchedule).subscribe({
      next: () => {
        this.calculateProductionScheduleHeight();
      },
      error: this.handleBackendError()
    });

    this.setBuggyLength();
    this.hasProductionScheduleLoaded = true;

    this.productionSchedulePlan.currentUpdatedProductionSchedule.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((productionSchedule: ProductionSchedule) => {
      if (!AssertionUtils.isNullOrUndefined(productionSchedule)) {
        this.productionSchedule = productionSchedule;
        this.calculateProductionScheduleHeight();
      }
    });

    this.productionSchedulePlan.didPatchProductionSchedulePlan.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => this.calculateProductionScheduleHeight());
  }

  private getQualitySettings(): void {
    this.machineQualities
      .getListOfCustomSettings()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((customSettings: PropertyValue[]) => {
        customSettings.forEach((setting: PropertyValue) => {
          if (setting.propertyName === 'missingDesignMarginInDents') {
            this.marginInDentsOfMissingDesigns = setting.propertyValue;
          }
        });
      });
  }

  private calculateProductionScheduleHeight(): void {
    this.productionScheduleHeight = convertCommercialUnit({
      from: {
        unit: Unit.MILLIMETER,
        value: this.productionSchedule.getProductionScheduleLengthInMillimeters()
      },
      to: this.commercialUnit
    });
  }

  private modifyProductionScheduleAfterVerify(productionSchedule: ProductionSchedule): void {
    assign(this.productionSchedule, ProductionSchedule.modifyProductionScheduleAfterVerify(this.productionSchedule, productionSchedule));
    this.productionSchedule.buggy = productionSchedule.buggy;
    this.productionSchedule.machine.roleCode = productionSchedule.machine.roleCode;
    this.productionSchedulePlan.initialiseProductionSchedule(this.productionSchedule).subscribe(() => {
      if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.buggy)) {
        this.setAvailableSpaceInBuggy();
      }
    });
  }

  private setBuggyLength(): void {
    const {buggyLengthMin: buggyLengthMinInMM, buggyLengthMax: buggyLengthMaxInMM} = this.productionSchedule.machineQuality;

    if (!AssertionUtils.isNullOrUndefined(buggyLengthMinInMM)) {
      this.buggyLengthMinInPicks = convertHeightInMeterToPicks(
        convertCommercialUnit({from: {unit: Unit.MILLIMETER, value: buggyLengthMinInMM}, to: Unit.METER}),
        this.productionSchedule?.machineQuality?.pickDensity * 1000,
        this.productionSchedule?.machineQuality?.weaveStructure
      );
      this.buggyLengthMaxInMM = buggyLengthMaxInMM;
    }

    if (!AssertionUtils.isNullOrUndefined(buggyLengthMaxInMM)) {
      this.buggyLengthMaxInPicks = convertHeightInMeterToPicks(
        convertCommercialUnit({from: {unit: Unit.MILLIMETER, value: buggyLengthMaxInMM}, to: Unit.METER}),
        this.productionSchedule?.machineQuality?.pickDensity * 1000,
        this.productionSchedule?.machineQuality?.weaveStructure
      );
    } else {
      this.buggyLengthMaxInPicks = convertHeightInMeterToPicks(
        convertCommercialUnit({from: {unit: Unit.MILLIMETER, value: 50000}, to: Unit.METER}),
        this.productionSchedule?.machineQuality?.pickDensity * 1000,
        this.productionSchedule?.machineQuality?.weaveStructure
      );
      this.buggyLengthMaxInMM = convertCommercialUnit({
        from: {unit: Unit.METER, value: convertPicksToHeightInMeter(this.buggyLengthMaxInPicks, this.productionSchedule?.machineQuality?.pickDensity * 1000)},
        to: Unit.MILLIMETER
      });
    }

    if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.buggy)) {
      this.setAvailableSpaceInBuggy();
    }
  }

  private handleBackendError(): (error: any) => void {
    return ErrorHandlers.navigateToOverviewAndThrowError(this.router, ProductionSchedulePlanComponent.urlToProductionScheduleOverview);
  }

  private subscribeToNavigationStateChanges(): void {
    this.sideNavigationStateService.onNavigationStateChanged.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      setTimeout(() => {
        this.updateProductionScheduleBuilderPageState();
      });
    });
  }

  private getProductionScheduleBuilderPageState(): void {
    this.productionScheduleBuilderPageStateManager
      .getForNewBuilder()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((productionScheduleBuilderState: ProductionScheduleNewBuilderPageState) => {
        this.productionScheduleBuilderState = productionScheduleBuilderState;
        if (AssertionUtils.isNullOrUndefined(productionScheduleBuilderState)) {
          this.updateProductionScheduleBuilderPageState();
        } else {
          this.initialPathLayoutHeightInPx = productionScheduleBuilderState.pathLayoutHeightInPx;
          this.initialPathLayoutWidthInPx = productionScheduleBuilderState.pathLayoutWidthInPx;
        }
      });
  }

  private updateProductionScheduleBuilderPageState(): void {
    this.productionScheduleBuilderPageStateManager
      .updateforNewBuilder({
        pathLayoutHeightInPx: this.pathLayout?.nativeElement.clientHeight ?? this.initialPathLayoutHeightInPx,
        pathLayoutWidthInPx: this.pathLayout?.nativeElement.clientWidth ?? this.initialPathLayoutWidthInPx
      } as ProductionScheduleNewBuilderPageState)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe();
  }

  private setAvailableSpaceInBuggy(): void {
    this.availableSpaceInBuggyInCommercialUnit = Math.floor(
      convertCommercialUnit({from: {unit: Unit.MILLIMETER, value: this.buggyLengthMaxInMM}, to: Unit.CENTIMETER}) -
        convertCommercialUnit({from: {unit: Unit.METER, value: this.productionSchedule.buggy?.amountPlannedInMeter ?? 0}, to: Unit.CENTIMETER})
    );
    this.availableSpaceInBuggyInPicks = this.buggyLengthMaxInPicks - (this.productionSchedule.buggy?.amountPlannedInPicks ?? 0);

    this.availableSpaceInBuggyForBuilderInPicks = this.availableSpaceInBuggyInPicks + this.pathLabelsOrNonProductionItemsFromLongestPathLengthInPicks;
    this.availableSpaceInBuggyForBuilderInCommercialUnit = this.availableSpaceInBuggyInCommercialUnit + this.pathLabelsOrNonProductionItemsFromLongestPathLengthInCommercialUnit;
  }

  private setWarningTextVisibility(): void {
    if (AssertionUtils.isNullOrUndefined(this.errorsAndWarningsContainer)) {
      return;
    }

    this.maxWarningWidth = this.errorsAndWarningsContainer.nativeElement.offsetWidth > this.maxWarningWidth ? this.errorsAndWarningsContainer.nativeElement.offsetWidth : this.maxWarningWidth;

    const warningMargin = parseInt(this.errorsAndWarningsContainer.nativeElement.style.marginRight, 10);
    const zoomInWidth = (this.zoomInContainer?.nativeElement.offsetWidth ?? 0) + (this.zoomInContainer ? parseInt(this.zoomInContainer.nativeElement.style.marginRight, 10) || 0 : 0);
    const zoomOutWidth = (this.zoomOutContainer?.nativeElement.offsetWidth ?? 0) + (this.zoomOutContainer ? parseInt(this.zoomOutContainer.nativeElement.style.marginRight, 10) || 0 : 0);
    const editPathsWidth =
      (this.editPathsToggleContainer?.nativeElement.offsetWidth ?? 0) + (this.editPathsToggleContainer ? parseInt(this.editPathsToggleContainer.nativeElement.style.marginRight, 10) || 0 : 0);

    const staticWidth = warningMargin + editPathsWidth + zoomInWidth + zoomOutWidth + (this.viewSelectorContainer?.nativeElement.offsetWidth ?? 0);

    this.canShowWarningWithText = this.buttonsContainer.nativeElement.offsetWidth > staticWidth + this.maxWarningWidth;
  }

  private setInitialPathLayoutDimentions(): void {
    const productionSchedulePlanWrapper = this.productionSchedulePlanWrapper.nativeElement;

    if (AssertionUtils.isNullOrUndefined(this.productionScheduleBuilderState) || !this.productionScheduleBuilderState.hasOwnProperty('pathLayoutHeightInPx')) {
      this.initialPathLayoutHeightInPx = this.INITIAL_PATH_LAYOUT_HEIGHT_IN_PX;
    }

    if (AssertionUtils.isNullOrUndefined(this.productionScheduleBuilderState) || !this.productionScheduleBuilderState.hasOwnProperty('pathLayoutWidthInPx')) {
      this.initialPathLayoutWidthInPx = productionSchedulePlanWrapper.clientWidth * 0.5 - this.resizerPx * 0.5;
    }

    this.initialPathLayoutMaxHeightInPx = productionSchedulePlanWrapper.clientHeight - (this.MIN_DRAWING_LIBRARY_HEIGHT_IN_PX + this.resizerPx);
    this.minPathContentWidthInPx = productionSchedulePlanWrapper.clientWidth * 0.4;
    this.minPathLayoutWidthInPx = productionSchedulePlanWrapper.clientWidth * 0.4;
  }
}
